<template>
  <div>
    <div v-if="!loading" class="conversation-input-container">
      <new-messenger-conversation-uploaded-files :upload-files="uploadFiles" @delete-file="deleteFile" />
      <div v-if="isJoined" id="text" class="conversation-input-placeholder">
        <div class="conversation-textarea-box">
          <b-input
            id="textarea"
            ref="textarea"
            v-model="textarea"
            type="textarea"
            :placeholder="$t('newMessenger.typeConversationMessage')"
            class="conversation-input"
            name="text"
            rows="1"
            @input="handleInput"
            @keydown.enter.exact.prevent.native="emitMessage"
          />
        </div>
        <input ref="fileInput" type="file" multiple class="conversation-input-file" @change="handleFileChange">
        <button class="conversation-input-button" @click="openFileInput">
          <b-icon type="is-primary" class="collapse-icon" icon="add-solid" />
        </button>
        <button class="conversation-input-button" @click="emitMessage">
          <b-loading :active="disabledInput || loading || $store.state.websocket.loadingTicket" :is-full-page="false" />
          <b-icon v-if="!disabledInput && !loading && !$store.state.websocket.loadingTicket" type="is-primary" class="collapse-icon" icon="send" />
        </button>
      </div>
      <div v-else>
        <b-button expanded type="is-primary" outlined :loading="$store.state.websocket.loadingAssignEmployees || $store.state.websocket.loadingTicket" @click="joinToTicket()">
          {{ $t('newMessenger.join') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import NewMessengerConversationUploadedFiles from '../NewMessengerConversationUploadedFiles.vue'
import debounce from '~/marketplace-front-core/utils/debounce'

export default {
  name: 'NewMessengerConversationInput',
  components: {
    NewMessengerConversationUploadedFiles
  },
  props: {
    uploadFiles: {
      type: Array,
      default: () => []
    },
    assignedEmployees: {
      type: Array,
      default: () => []
    },
    ticketId: {
      type: Number,
      default: null
    },
    clientTicketId: {
      type: Number,
      default: null
    },
    ticketStatus: {
      type: String,
      default: 'OPEN'
    },
    disabledInput: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      textarea: '',
      textareaHeight: 'auto',
      client: null,
      loading: true,
      typing: false,
      bufferTextarea: {}
    }
  },
  computed: {
    isJoined () {
      const storeAssigned = this.assignedEmployees.filter(x => x.isMe)
      return !!storeAssigned.length
    }
  },
  watch: {
    ticketId (newId, lastId) {
      this.stopTyping()
      this.changeTicket(newId, lastId)
    },
    textarea () {
      this.setHeightTextarea()
    }
  },
  mounted () {
    this.loading = false
  },
  methods: {
    changeTicket (newId, lastId) {
      if (lastId) {
        if (this.textarea && this.textarea.length) {
          this.bufferTextarea[lastId] = this.textarea
        } else {
          delete this.bufferTextarea[lastId]
        }
      }

      if (this.bufferTextarea[newId]) {
        this.textarea = this.bufferTextarea[newId]
      } else {
        this.textarea = ''
      }
    },
    setHeightTextarea () {
      this.textareaHeight = 'auto'
      this.$nextTick(() => {
        const textarea = document.getElementById('textarea')
        if (textarea) {
          textarea.style.height = this.textareaHeight
          if (textarea.scrollHeight > textarea.offsetHeight) {
            this.textareaHeight = `${textarea.scrollHeight}px`
            textarea.style.height = this.textareaHeight
          }
        }
      })
    },
    joinToTicket () {
      this.$store.dispatch('websocket/assignRoomTo', { assignTo: [true], unassignFrom: [], ticketId: this.ticketId })
    },
    handleInput () {
      if (!this.typing) {
        this.typing = true
        this.$store.dispatch('websocket/typing', { action: 'start-typing' })
      }
      debounce(function (date) {
        this.stopTyping()
      }, 3000)
    },
    stopTyping () {
      this.$store.dispatch('websocket/typing', { action: 'stop-typing' })
      this.typing = false
    },
    openFileInput () {
      this.$refs.fileInput.click()
    },
    handleFileChange (event) {
      this.$emit('files', Array.from(event.target.files))
      this.$refs.fileInput.value = null
    },
    emitMessage () {
      if ((this.textarea.trim() !== '' || this.uploadFiles.length > 0) && !this.disabledInput) {
        this.$emit('append', this.textarea)
        this.textarea = ''
        this.textareaHeight = 'auto'
      }
    },
    deleteFile (index) {
      this.$emit('delete-file', index)
    }
  }
}
</script>

<style lang="scss" scoped>
$messenger-conversation-input-bg-color: #EAEAEA;
$messanger-delivery-color: #179644;
$messenger-textarea-bg-color: #EBE9EC;
$messenger-textarea-scrollbar-color: #ccc;
$messenger-textarea-scrollbar-hover-color: #aaa;

.conversation-input-container {
  padding: 8px 16px;
  width: 100%;
}

.conversation-input-placeholder {
  display: flex;
  margin-top: 10px;
  margin-bottom: 10px;
  align-items: center;

  .conversation-input-file {
    display: none;
  }
}

.conversation-textarea-box {
  width: 100%;
  margin-right: 8px;
}

// .conversation-textarea-box::after {
//   content: attr(data-replicated-value)" ";
//   white-space: pre-wrap;
//   visibility: hidden;
// }

.conversation-input {
  width: 100%;
  padding: 4px 10px;
  resize: none;
  overflow-y: none;
  border-radius: 20px;
  border: 8px solid transparent;
  background-color: $messenger-textarea-bg-color;

  :deep(textarea) {
    padding: 0;
    max-height: 184px;
    line-height: 24px;
    background-color: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }
}

textarea::-webkit-scrollbar {
  width: 10px;
  background-color: transparent;
}

textarea::-webkit-scrollbar-thumb {
  background-color: $messenger-textarea-scrollbar-color;
  border-radius: 5px;
}

textarea::-webkit-scrollbar-thumb:hover {
  background-color: $messenger-textarea-scrollbar-hover-color;
}

.conversation-input-button {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.7rem;
  width: 2.7rem;
  border-radius: 100%;
  background: transparent;
  border: none;
  :deep(.loading-background) {
    background: none;
  }
  :deep(.loading-icon:after) {
    top: calc(50% - 16px);
    left: calc(50% - 16px);
    width: 32px;
    height: 32px;
    border-width: 4px;
  }
  :active {
    color: $messanger-delivery-color;
    :deep(.icon) > i {
      font-size: 46px !important;
    }
  }
  :deep(.icon) > i {
    font-size: 24px !important;
  }
}
</style>
